import { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Container, Paper, Typography,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField,
    MenuItem, Chip, IconButton, Tooltip,
    InputAdornment, Alert, CircularProgress,
    Switch, FormControlLabel
} from '@mui/material';
import {
    Add, Edit, Refresh, Visibility, VisibilityOff,
    AdminPanelSettings, Person, CheckCircle, Cancel
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SapService } from '../services/api';

const ROLES = [
    { value: 'admin', label: 'Administrador' },
    { value: 'usuario', label: 'Usuario' },
];

const initialForm = {
    email: '',
    nombre: '',
    password: '',
    rol: 'usuario',
    activo: true,
};

export default function Configuracion() {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');

    const cargarUsuarios = useCallback(async () => {
        setLoading(true);
        try {
            const data = await SapService.getUsuarios();
            setUsuarios(Array.isArray(data) ? data : []);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los usuarios.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarUsuarios();
    }, [cargarUsuarios]);

    const handleOpenCrear = () => {
        setForm(initialForm);
        setEditMode(false);
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleOpenEditar = (usuario) => {
        setForm({
            email: usuario.email,
            nombre: usuario.nombre || '',
            password: '',
            rol: usuario.rol || 'usuario',
            activo: usuario.activo !== false,
        });
        setEditMode(true);
        setFormError('');
        setShowPassword(false);
        setOpenDialog(true);
    };

    const handleClose = () => {
        setOpenDialog(false);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (formError) setFormError('');
    };

    const handleGuardar = async () => {
        setFormError('');

        // Validaciones
        if (!form.nombre.trim()) { setFormError('El nombre es requerido.'); return; }
        if (!form.rol) { setFormError('El rol es requerido.'); return; }

        if (!editMode) {
            if (!form.email.trim()) { setFormError('El email es requerido.'); return; }
            if (!form.email.includes('@')) { setFormError('El email no es válido.'); return; }
            if (!form.password) { setFormError('La contraseña es requerida.'); return; }
            if (form.password.length < 6) { setFormError('La contraseña debe tener al menos 6 caracteres.'); return; }
        }

        if (editMode && form.password && form.password.length < 6) {
            setFormError('La contraseña debe tener al menos 6 caracteres.');
            return;
        }

        setSaving(true);
        try {
            if (!editMode) {
                await SapService.crearUsuario({
                    email: form.email.trim().toLowerCase(),
                    nombre: form.nombre.trim(),
                    password: form.password,
                    rol: form.rol,
                });
                Swal.fire({ icon: 'success', title: 'Usuario creado', text: `${form.nombre} fue creado exitosamente.`, timer: 2000, showConfirmButton: false });
            } else {
                const payload = {
                    nombre: form.nombre.trim(),
                    rol: form.rol,
                    activo: form.activo,
                };
                if (form.password) payload.password = form.password;
                await SapService.actualizarUsuario(form.email, payload);
                Swal.fire({ icon: 'success', title: 'Usuario actualizado', text: `${form.nombre} fue actualizado exitosamente.`, timer: 2000, showConfirmButton: false });
            }
            setOpenDialog(false);
            cargarUsuarios();
        } catch (error) {
            setFormError(error.message || 'Ocurrió un error. Intente de nuevo.');
        } finally {
            setSaving(false);
        }
    };

    const getRolChip = (rol) => {
        const isAdmin = rol === 'admin';
        return (
            <Chip
                icon={isAdmin ? <AdminPanelSettings sx={{ fontSize: '0.9rem !important' }} /> : <Person sx={{ fontSize: '0.9rem !important' }} />}
                label={isAdmin ? 'Administrador' : 'Usuario'}
                size="small"
                sx={{
                    bgcolor: isAdmin ? '#fef3c7' : '#eff6ff',
                    color: isAdmin ? '#92400e' : '#1e40af',
                    border: `1px solid ${isAdmin ? '#fde68a' : '#bfdbfe'}`,
                    fontWeight: 600,
                    '& .MuiChip-icon': { color: 'inherit' }
                }}
            />
        );
    };

    const getActivoChip = (activo) => (
        <Chip
            icon={activo ? <CheckCircle sx={{ fontSize: '0.85rem !important' }} /> : <Cancel sx={{ fontSize: '0.85rem !important' }} />}
            label={activo ? 'Activo' : 'Inactivo'}
            size="small"
            sx={{
                bgcolor: activo ? '#dcfce7' : '#fee2e2',
                color: activo ? '#166534' : '#991b1b',
                border: `1px solid ${activo ? '#bbf7d0' : '#fecaca'}`,
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'inherit' }
            }}
        />
    );

    return (
        <Container maxWidth="lg" sx={{ mt: 5, mb: 10 }}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid rgba(0,0,0,0.08)' }}>

                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} sx={{ color: '#0f172a' }}>
                            Gestión de Usuarios
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Administra los accesos al sistema de creación de materiales.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Tooltip title="Recargar">
                            <IconButton onClick={cargarUsuarios} disabled={loading} sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2 }}>
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleOpenCrear}
                            sx={{
                                bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' },
                                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(211,47,47,0.3)'
                            }}
                        >
                            Nuevo usuario
                        </Button>
                    </Box>
                </Box>

                {/* Tabla */}
                <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Nombre</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Rol</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#374151' }} align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                        <CircularProgress size={32} sx={{ color: '#D32F2F' }} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                            Cargando usuarios...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : usuarios.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        No hay usuarios registrados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                usuarios.map((u) => (
                                    <TableRow key={u.email} hover sx={{ '&:last-child td': { border: 0 } }}>
                                        <TableCell sx={{ fontWeight: 500, color: '#0f172a' }}>
                                            {u.nombre}
                                        </TableCell>
                                        <TableCell sx={{ color: '#475569', fontSize: '0.875rem' }}>
                                            {u.email}
                                        </TableCell>
                                        <TableCell>{getRolChip(u.rol)}</TableCell>
                                        <TableCell>{getActivoChip(u.activo)}</TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar usuario">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenEditar(u)}
                                                    sx={{
                                                        color: '#3b82f6',
                                                        bgcolor: '#eff6ff',
                                                        '&:hover': { bgcolor: '#dbeafe' },
                                                        borderRadius: 1.5
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Typography variant="caption" color="text.disabled" sx={{ mt: 2, display: 'block' }}>
                    {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} registrado{usuarios.length !== 1 ? 's' : ''}
                </Typography>
            </Paper>

            {/* Dialog crear / editar */}
            <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                        {editMode ? 'Editar usuario' : 'Nuevo usuario'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {editMode
                            ? 'Modifica los datos del usuario. Deja la contraseña vacía para no cambiarla.'
                            : 'Completa los datos para crear el acceso.'}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers sx={{ pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {formError && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>{formError}</Alert>
                    )}

                    {/* Email — solo en creación */}
                    {!editMode && (
                        <TextField
                            label="Correo electrónico"
                            type="email"
                            fullWidth
                            value={form.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            placeholder="usuario@talsa.com.co"
                            required
                        />
                    )}
                    {editMode && (
                        <TextField
                            label="Correo electrónico"
                            value={form.email}
                            fullWidth
                            disabled
                            sx={{ '& .MuiInputBase-root': { bgcolor: '#f8fafc' } }}
                        />
                    )}

                    <TextField
                        label="Nombre completo"
                        fullWidth
                        value={form.nombre}
                        onChange={(e) => handleChange('nombre', e.target.value)}
                        placeholder="Ej: Juan Pérez"
                        required
                    />

                    <TextField
                        label={editMode ? 'Nueva contraseña (opcional)' : 'Contraseña'}
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        value={form.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        placeholder={editMode ? 'Dejar vacío para no cambiar' : 'Mínimo 6 caracteres'}
                        required={!editMode}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        select
                        label="Rol"
                        fullWidth
                        value={form.rol}
                        onChange={(e) => handleChange('rol', e.target.value)}
                    >
                        {ROLES.map(r => (
                            <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                        ))}
                    </TextField>

                    {/* Estado activo — solo en edición */}
                    {editMode && (
                        <Box sx={{ px: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.activo}
                                        onChange={(e) => handleChange('activo', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16a34a' }
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" fontWeight={500}>
                                        {form.activo ? 'Usuario activo' : 'Usuario inactivo'}
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={handleClose} disabled={saving} sx={{ textTransform: 'none', borderRadius: 2 }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGuardar}
                        disabled={saving}
                        sx={{
                            bgcolor: '#D32F2F', '&:hover': { bgcolor: '#B71C1C' },
                            textTransform: 'none', borderRadius: 2, fontWeight: 600, minWidth: 110
                        }}
                    >
                        {saving ? <CircularProgress size={20} color="inherit" /> : editMode ? 'Guardar' : 'Crear usuario'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
